import React, { useState, useEffect } from 'react';
import { 
  Tag, Heart, Star, MapPin, ArrowLeft, Maximize2, CheckCheck,
  Car, Settings, Star as StarIcon, Calendar, Gauge, Fuel, Users, 
  CreditCard, Clock, UserCheck, Shield, List, Hash, ChevronDown, Lock,
  X, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../constants/routes.constants';
import KYCModal from '../kyc/KYCModal';
import { getImageUrl, getImageUrls } from '../../utils/imageUtils';

const VehicleModal = ({ vehicle, onClose, isLoggedIn = false, onLoginClick }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, user, refreshUser } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [showKYCModal, setShowKYCModal] = useState(false);
  
  // Utiliser isAuthenticated du contexte si disponible, sinon utiliser la prop
  const userIsLoggedIn = isAuthenticated || isLoggedIn;
  
  // Vérifier si le KYC est validé
  const isKYCValid = user?.kycStatus === 'VERIFIED' || user?.kycVerified === true;

  // Rafraîchir les données utilisateur quand le modal s'ouvre pour avoir les dernières données KYC
  useEffect(() => {
    if (userIsLoggedIn && refreshUser && !isKYCValid) {
      // Rafraîchir seulement si le KYC n'est pas encore validé
      refreshUser();
    }
  }, [userIsLoggedIn]); // Seulement quand l'utilisateur se connecte

  // Bloquer le scroll de la page quand le modal est ouvert
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Liste des images du véhicule (utiliser getImageUrls pour gérer les URLs absolues et relatives)
  const rawImages = vehicle.images && vehicle.images.length > 0 
    ? vehicle.images 
    : vehicle.image 
      ? [vehicle.image]
      : [];
  const images = rawImages.length > 0 
    ? getImageUrls(rawImages)
    : [
        'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?auto=format&fit=crop&w=500&q=80',
        'https://images.unsplash.com/photo-1606152421802-db97b9c7a11b?auto=format&fit=crop&w=500&q=80',
        'https://images.unsplash.com/photo-1555215695-3004980adade?auto=format&fit=crop&w=500&q=80',
      ];

  // Caractéristiques du véhicule (specs) - dynamiques avec fallbacks
  const transmissionDisplay =
    vehicle.transmission ||
    (vehicle.specs?.transmission === 'AUTOMATIC'
      ? 'Automatique'
      : vehicle.specs?.transmission === 'MANUAL'
        ? 'Manuelle'
        : vehicle.specs?.transmission || 'Non spécifié');

  const mileageDisplay =
    vehicle.mileage ||
    vehicle.specs?.odometer?.name ||
    (typeof vehicle.specs?.odometer === 'string' ? vehicle.specs.odometer : null) ||
    'N/A';

  const seatsDisplay = vehicle.seats || vehicle.specs?.seats || 'N/A';

  const brandDisplay =
    vehicle.make ||
    vehicle.specs?.brand?.name ||
    (typeof vehicle.specs?.brand === 'string' ? vehicle.specs.brand : null) ||
    'N/A';

  const modelDisplay =
    vehicle.model ||
    vehicle.specs?.model?.name ||
    (typeof vehicle.specs?.model === 'string' ? vehicle.specs.model : null) ||
    'N/A';

  const yearDisplay = vehicle.year || vehicle.specs?.year || 'N/A';

  const plateDisplay =
    vehicle.licencePlateNumber ||
    vehicle.license_plate ||
    vehicle.registration_number ||
    'N/A';

  const minDaysDisplay =
    vehicle.minRentalDays ||
    vehicle.min_days ||
    'N/A';

  const minAgeDisplay =
    vehicle.ageRestriction?.minimumAge ||
    vehicle.min_age ||
    'N/A';

  const insuranceDisplay =
    vehicle.insuranceCoverage ||
    vehicle.insurance ||
    'N/A';

  const typeDisplay =
    (typeof vehicle.type === 'string' ? vehicle.type : vehicle.type?.title) || 'N/A';

  const fuelDisplay =
    vehicle.fuel ||
    vehicle.specs?.fuel?.name ||
    (typeof vehicle.specs?.fuel === 'string' ? vehicle.specs.fuel : null) ||
    'N/A';

  const specs = [
    
    { icon: Settings, label: t('vehicleDetail.brand'), value: brandDisplay },
    { icon: StarIcon, label: t('vehicleDetail.model'), value: modelDisplay },
    { icon: Calendar, label: t('vehicleDetail.year'), value: yearDisplay },
    { icon: Gauge, label: t('vehicleDetail.transmission'), value: transmissionDisplay },
    { icon: Gauge, label: t('vehicleDetail.mileage'), value: mileageDisplay },
    { icon: Fuel, label: t('vehicleDetail.fuelType'), value: fuelDisplay },
    { icon: Users, label: t('vehicleDetail.seats'), value: seatsDisplay },
    { icon: CreditCard, label: t('vehicleDetail.plateNumber'), value: plateDisplay },
    { icon: Clock, label: t('vehicleDetail.minRentalDays'), value: minDaysDisplay },
    { icon: UserCheck, label: t('vehicleDetail.minAge'), value: minAgeDisplay },
    { icon: Shield, label: t('vehicleDetail.insurance'), value: insuranceDisplay },
  ];

  // Caractéristiques de la voiture (features) - depuis l'API
  // vehicle.features est un tableau d'objets avec 'name' (ou peut-être juste des strings)
  const features = vehicle.features && vehicle.features.length > 0
    ? vehicle.features.map(feature => {
        // Si c'est un objet avec 'name', utiliser 'name', sinon utiliser la valeur directement
        return typeof feature === 'object' && feature !== null ? (feature.name || feature.description || JSON.stringify(feature)) : feature;
      })
    : [];

  // Règles du véhicule - depuis l'API
  // vehicle.rules est un tableau d'objets avec 'name' (ou peut-être juste des strings)
  const rules = vehicle.rules && vehicle.rules.length > 0
    ? vehicle.rules.map(rule => {
        // Si c'est un objet avec 'name', utiliser 'name', sinon utiliser la valeur directement
        return typeof rule === 'object' && rule !== null ? (rule.name || rule.description || JSON.stringify(rule)) : rule;
      })
    : [];

  // Politique d'annulation - depuis l'API
  // vehicle.cancellationPolicies est un tableau d'objets avec 'policy' (objet) et 'percentage'
  const cancellationPolicies = vehicle.cancellationPolicies && vehicle.cancellationPolicies.length > 0
    ? vehicle.cancellationPolicies.map(policyObj => {
        const percentage = policyObj.percentage || 0;
        const policy = policyObj.policy || {};
        const description = policy.description || policy.name || '';
        
        // Remplacer x% par le pourcentage réel dans la description
        const formattedDescription = description.replace(/x%/gi, `${percentage}%`);
        
        // Si pas de description, créer un message par défaut
        if (!formattedDescription) {
          return `${percentage}% deduction will apply if canceled`;
        }
        
        return formattedDescription;
      })
    : [];

  // Gestion du clic sur "Suivant"
  const handleNextClick = async () => {
    // Si l'utilisateur n'est pas connecté, rediriger vers la page de login
    if (!userIsLoggedIn) {
      // Fermer le modal d'abord
      onClose();
      // Rediriger vers la page de login
      navigate(ROUTES.LOGIN);
      return;
    }

    // Rafraîchir les données utilisateur avant de vérifier le KYC pour avoir les dernières données
    let currentUser = user;
    if (refreshUser) {
      const updatedUser = await refreshUser();
      if (updatedUser) {
        currentUser = updatedUser;
      }
    }

    // Vérifier le KYC avec les données les plus récentes
    const isKYCValidNow = currentUser?.kycStatus === 'VERIFIED' || currentUser?.kycVerified === true;
    
    if (!isKYCValidNow) {
      // Ouvrir le modal KYC
      setShowKYCModal(true);
      return;
    }

    // Navigate to booking page with vehicle data
    const vehicleId = vehicle?._id || vehicle?.id;
    
    if (!vehicle || !vehicleId) {
      console.error('Vehicle ID is missing:', vehicle);
      alert('Erreur: Informations du véhicule manquantes');
      return;
    }

    const bookingUrl = ROUTES.BOOKING_BY_ID(vehicleId);
    console.log('Navigating to booking page:', bookingUrl, 'with vehicle ID:', vehicleId);
    
    // Fermer le modal d'abord
    onClose();
    
    // Utiliser setTimeout pour s'assurer que le modal est fermé avant la navigation
    setTimeout(() => {
      try {
        navigate(bookingUrl, {
          state: { vehicle }
        });
      } catch (error) {
        console.error('Navigation error:', error);
        // Fallback: utiliser window.location si navigate échoue
        window.location.href = bookingUrl;
      }
    }, 100);
  };

  // Gestion de la soumission du KYC
  const handleKYCSubmit = async (formData) => {
    // La fonction est gérée directement dans KYCModal
    // Cette fonction est juste un callback
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative bg-gray-50 rounded-3xl overflow-hidden w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
        
        {/* Bouton Fermer - Fixe */}
        <button 
          onClick={onClose}
          className="absolute top-4 left-4 z-20 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        
        {/* Bouton Favoris - Fixe */}
        <button 
          onClick={() => setIsFavorite(!isFavorite)}
          className="absolute top-4 right-4 z-20 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition"
        >
          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-700'}`} />
        </button>

        {/* Contenu scrollable */}
        <div className="overflow-y-auto scrollbar-hide flex-1 pb-20">
          {/* Image principale */}
          <div className="relative h-64">
            <img 
              src={images[selectedImage]} 
              alt={vehicle.model}
              className="w-full h-full object-cover"
            />

            {/* Bouton Plein écran */}
            <button 
              onClick={() => setShowFullscreen(true)}
              className="absolute bottom-4 right-4 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition"
            >
              <Maximize2 className="w-4 h-4 text-gray-700" />
            </button>
          </div>

          {/* Miniatures */}
          <div className="bg-white px-4 py-3">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-16 rounded-xl overflow-hidden border-2 transition ${
                    selectedImage === index 
                      ? 'border-[#27489E]' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img 
                    src={img} 
                    alt={`Photo ${index + 1}`} 
                    className="w-full h-full object-cover" 
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Contenu */}
          <div className="p-4">
            {/* Titre et Rating */}
            <div className="bg-white rounded-2xl p-4 mb-3">
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-2xl font-bold text-gray-900">{vehicle.model}</h2>
                <div className="flex items-center gap-1">
                  <span className="text-[#27489E] font-bold text-lg">
                    {(typeof vehicle.rating === 'number' && !isNaN(vehicle.rating) ? vehicle.rating : 4.5).toFixed(1)}
                  </span>
                  <Star className="w-5 h-5 fill-[#F5A623] text-[#F5A623]" />
                </div>
              </div>
              {/* Localisation */}
              <div className="flex items-center gap-2 text-gray-500">
                <MapPin className="w-4 h-4 text-[#27489E]" />
                <span className="text-sm">
                  {typeof vehicle.location === 'string' 
                    ? vehicle.location 
                    : (vehicle.location?.city || vehicle.vehicleLocation?.cityName || 'N/A') + ', ' + t('common.morocco')}
                </span>
              </div>
            </div>

            {/* Offres spéciales (Remises Dynamiques) */}
            {(vehicle.pricing?.weeklyDiscount || vehicle.pricing?.monthlyDiscount) && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 mb-3 shadow-sm">
                <h4 className="text-sm font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-indigo-600" />
                  Offres spéciales
                </h4>
                <div className="flex flex-wrap gap-2">
                  {vehicle.pricing?.weeklyDiscount && (
                    <span className="inline-flex items-center px-3 py-1 bg-white text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold shadow-sm">
                      {vehicle.pricing.weeklyDiscount.type === 'PERCENTAGE'
                        ? `Remise 7j : ${vehicle.pricing.weeklyDiscount.value}%`
                        : `Remise 7j : ${vehicle.pricing.weeklyDiscount.value} ${vehicle.pricing?.currency || 'MAD'}`}
                    </span>
                  )}
                  {vehicle.pricing?.monthlyDiscount && (
                    <span className="inline-flex items-center px-3 py-1 bg-white text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold shadow-sm">
                      {vehicle.pricing.monthlyDiscount.type === 'PERCENTAGE'
                        ? `Remise 30j : ${vehicle.pricing.monthlyDiscount.value}%`
                        : `Remise 30j : ${vehicle.pricing.monthlyDiscount.value} ${vehicle.pricing?.currency || 'MAD'}`}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Catégories Multiples */}
            <div className="mb-4">
               <div className="flex flex-wrap gap-2">
                {(vehicle.categories || vehicle.category) && (vehicle.categories || vehicle.category).length > 0 ? (
                  (vehicle.categories || vehicle.category).map((cat, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-50 text-[#27489E] border border-[#27489E] rounded-full text-xs font-bold">
                      {typeof cat === 'object' ? (cat.name || cat.title || 'N/A') : cat}
                    </span>
                  ))
                ) : (
                  <span className="px-3 py-1 bg-blue-50 text-[#27489E] border border-[#27489E] rounded-full text-xs font-bold">
                    {typeDisplay}
                  </span>
                )}
              </div>
            </div>

            {/* Grille des caractéristiques */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {specs.map((spec, index) => (
                <div key={index} className="bg-white rounded-xl p-3 flex items-center gap-3">
                  <spec.icon className="w-6 h-6 text-[#27489E] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs text-gray-500 truncate">{spec.label}</div>
                    <div className="font-semibold text-gray-800 text-sm truncate">{spec.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Caractéristiques de la voiture */}
            <div className="bg-white rounded-2xl p-4 mb-3">
              <h3 className="text-lg font-bold text-gray-900 mb-3">{t('vehicleDetail.features')}</h3>
              <div className="space-y-2.5">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCheck className="w-5 h-5 text-[#27489E]" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Règles du véhicule - Accordéon */}
            <div className="bg-white rounded-2xl overflow-hidden mb-3">
              <button 
                onClick={() => setRulesOpen(!rulesOpen)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <List className="w-5 h-5 text-[#27489E]" />
                  <span className="font-semibold text-gray-800">{t('vehicleDetail.rules')}</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${rulesOpen ? 'rotate-180' : ''}`} />
              </button>
              {rulesOpen && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                  <div className="space-y-3 mt-3">
                    {rules.map((rule, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCheck className="w-5 h-5 text-[#27489E] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm leading-relaxed">{rule}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Politique d'annulation - Accordéon */}
            <div className="bg-white rounded-2xl overflow-hidden mb-3">
              <button 
                onClick={() => setPolicyOpen(!policyOpen)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <Hash className="w-5 h-5 text-[#27489E]" />
                  <span className="font-semibold text-gray-800">{t('vehicleDetail.cancellationPolicy')}</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${policyOpen ? 'rotate-180' : ''}`} />
              </button>
              {policyOpen && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                  <div className="space-y-3 mt-3">
                    {cancellationPolicies.map((policy, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCheck className="w-5 h-5 text-[#27489E] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm leading-relaxed">{policy}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Proposé par - Profil agence */}
            <div className="bg-white rounded-2xl p-4 mb-3">
              <div className="flex items-center gap-4">
                {/* Avatar agence */}
                <div className="w-12 h-12 bg-[#E85D04] rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  {vehicle.agency.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-gray-900">{t('vehicleDetail.proposedBy')} {vehicle.agency.name}</div>
                  {!showLoginPrompt && (
                    <button 
                      onClick={() => {
                        if (!isLoggedIn) {
                          setShowLoginPrompt(true);
                        }
                      }}
                      className="text-[#27489E] font-medium text-sm hover:underline"
                    >
                      {t('vehicleDetail.viewProfile')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Message connexion pour voir coordonnées - S'affiche au clic */}
            {showLoginPrompt && !isLoggedIn && (
              <div className="bg-white rounded-2xl p-6 text-center border-2 border-dashed border-gray-200">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Lock className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  {t('auth.loginToSeeContact')}
                </p>
                <button 
                  onClick={() => {
                    if (onLoginClick) {
                      onClose(); // Fermer le modal véhicule d'abord
                      onLoginClick(); // Puis ouvrir l'AuthPage
                    }
                  }}
                  className="w-full bg-[#27489E] text-white py-3 rounded-xl font-semibold hover:bg-[#1e3a7d] transition"
                >
                  {t('auth.login')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer fixe avec prix aligné */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-3xl shrink-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-baseline gap-1">
              <span className="text-[#27489E] font-bold text-2xl flex-shrink-0 whitespace-nowrap">MAD {vehicle.price ? vehicle.price.toFixed(2).replace('.', ',') : '0,00'}</span>
              <span className="text-gray-500 text-sm flex-shrink-0 whitespace-nowrap">/ {t('vehicles.perDay')}</span>
            </div>
            <button 
              onClick={handleNextClick}
              className="bg-[#27489E] text-white px-8 py-3 rounded-full font-bold hover:bg-[#1e3a7d] transition shadow-md whitespace-nowrap flex-shrink-0"
            >
              {t('vehicleDetail.next')}
            </button>
          </div>
        </div>
      </div>

      {/* Galerie Plein Écran */}
      {showFullscreen && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4">
            <button 
              onClick={() => setShowFullscreen(false)}
              className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <span className="text-white font-medium">
              {selectedImage + 1} / {images.length}
            </span>
            <div className="w-10"></div>
          </div>

          {/* Image principale */}
          <div className="flex-1 flex items-center justify-center relative px-4">
            {/* Bouton précédent */}
            <button 
              onClick={() => setSelectedImage(prev => prev === 0 ? images.length - 1 : prev - 1)}
              className="absolute left-4 bg-white/10 hover:bg-white/20 p-3 rounded-full transition z-10"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>

            {/* Image */}
            <img 
              src={images[selectedImage]} 
              alt={`Photo ${selectedImage + 1}`}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />

            {/* Bouton suivant */}
            <button 
              onClick={() => setSelectedImage(prev => prev === images.length - 1 ? 0 : prev + 1)}
              className="absolute right-4 bg-white/10 hover:bg-white/20 p-3 rounded-full transition z-10"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Miniatures en bas */}
          <div className="p-4">
            <div className="flex gap-2 justify-center overflow-x-auto scrollbar-hide">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition ${
                    selectedImage === index 
                      ? 'border-white' 
                      : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                >
                  <img 
                    src={img} 
                    alt={`Miniature ${index + 1}`} 
                    className="w-full h-full object-cover" 
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal KYC */}
      {showKYCModal && (
        <KYCModal
          onClose={async () => {
            setShowKYCModal(false);
            // Rafraîchir les données utilisateur après fermeture du modal KYC
            if (refreshUser) {
              await refreshUser();
            }
          }}
          onSubmit={handleKYCSubmit}
        />
      )}
    </div>
  );
};

export default VehicleModal;
